import { type Message, Role } from "@copilotkit/runtime-client-gql";
import Box from "@memorang/ui/src/components/Box";
import { useAppTheme } from "@memorang/ui/src/useAppTheme";
import { ScrollView, View } from "react-native";

/* --------------- Hooks --------------- */
import { useCopilotChat } from "@copilotkit/react-core";
import { memo, useEffect, useMemo, useRef } from "react";
import { ActionRenderer } from "../ActionRenderer";
import { MessageContainer } from "../MessageContainer";

const removeSystemMessage = (message: Message) => {
	if (message.isTextMessage()) {
		return message.role !== Role.System;
	}
	return true;
};

const MemoizedMessageContainer = memo(MessageContainer);
const MemoizedActionRenderer = memo(ActionRenderer);
const MessageItem = memo(({ message }: { message: Message }) => {
	if (message.isTextMessage() && !!message.content) {
		return <MemoizedMessageContainer message={message} />;
	}

	if (message.isResultMessage()) {
		return <MemoizedActionRenderer message={message} />;
	}

	return null;
});

export const Messages = () => {
	const theme = useAppTheme();
	const { visibleMessages } = useCopilotChat();

	const chatMessages = useMemo(() => {
		return visibleMessages.filter(removeSystemMessage);
	}, [visibleMessages]);

	const scrollViewRef = useRef<ScrollView | null>(null);
	const endRef = useRef<View | null>(null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This is a valid useEffect
	useEffect(() => {
		if (scrollViewRef.current) {
			scrollViewRef.current.scrollToEnd({ animated: false });
		}
	}, [chatMessages]);

	return (
		<ScrollView
			ref={scrollViewRef}
			showsVerticalScrollIndicator={false}
			focusable
		>
			<Box flex={1} backgroundColor={theme.colors.surface} gap={16} padding={8}>
				{chatMessages.map((message) => (
					<MessageItem key={message.id} message={message} />
				))}
				<View style={{ height: 16 }} ref={endRef} />
			</Box>
		</ScrollView>
	);
};
